import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Loader2, ArrowRight } from 'lucide-react';
import { apiGet } from '../data/api';
import t from '../i18n/it';

function SportBadge({ sport }) {
  return sport === 'tennis' ? (
    <span className="px-1.5 py-0.5 text-xs rounded bg-blue-500/15 text-blue-400">
      {t.common.tennis}
    </span>
  ) : (
    <span className="px-1.5 py-0.5 text-xs rounded bg-purple-500/15 text-purple-400">
      {t.common.table_tennis}
    </span>
  );
}

function FormScoreBar({ score }) {
  const pct = Math.min(score, 100);
  const color = score >= 80 ? '#10B981' : score >= 60 ? '#F59E0B' : '#EF4444';
  return (
    <div className="flex items-center gap-2">
      <div className="w-14 h-1.5 rounded-full bg-zinc-800">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span
        className="text-xs tabular-nums text-zinc-300"
        style={{ fontFamily: 'JetBrains Mono, monospace' }}
      >
        {score?.toFixed(0)}
      </span>
    </div>
  );
}

export default function Players() {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sportFilter, setSportFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    setLoading(true);
    const query = sportFilter ? `?sport=${sportFilter}` : '';
    apiGet(`/players${query}`)
      .then(setPlayers)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [sportFilter]);

  const filtered = players.filter((p) =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-5 fade-in">
      <div>
        <h1
          className="text-4xl font-bold text-zinc-50 tracking-tight"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          {t.players.title}
        </h1>
        <p className="text-zinc-400 mt-1 text-sm">{t.players.subtitle}</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          data-testid="player-search"
          type="text"
          placeholder={t.players.search_placeholder}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="px-3 py-2 text-sm rounded-sm text-zinc-300 placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-emerald-500 w-52"
          style={{ background: '#18181B', border: '1px solid #27272A' }}
        />
        <select
          data-testid="players-filter-sport"
          value={sportFilter}
          onChange={(e) => setSportFilter(e.target.value)}
          className="px-3 py-2 text-sm rounded-sm text-zinc-300 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          style={{ background: '#18181B', border: '1px solid #27272A' }}
        >
          <option value="">{t.players.all_sports}</option>
          <option value="tennis">{t.common.tennis}</option>
          <option value="table_tennis">{t.common.table_tennis}</option>
        </select>
        <span className="ml-auto text-xs text-zinc-500">
          {filtered.length} {t.common.players_count}
        </span>
      </div>

      {/* Table */}
      <div
        className="rounded-sm overflow-hidden"
        style={{ background: '#18181B', border: '1px solid #27272A' }}
      >
        {loading ? (
          <div className="flex items-center gap-2 justify-center py-16 text-zinc-500">
            <Loader2 size={18} className="animate-spin" />
            <span>{t.common.loading}</span>
          </div>
        ) : error ? (
          <div className="text-red-400 text-center py-10">{t.common.error}</div>
        ) : filtered.length === 0 ? (
          <div className="text-zinc-600 text-center py-16 text-sm">
            <Users size={32} className="mx-auto mb-3 opacity-30" />
            {t.players.no_players}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid #27272A' }}>
                {[
                  t.players.rating,
                  t.players.name,
                  t.players.country,
                  t.common.sport,
                  t.players.form_score,
                  t.players.surface_hard,
                  t.players.surface_clay,
                  t.players.surface_grass,
                  '',
                ].map((h, i) => (
                  <th
                    key={i}
                    className={`py-3 text-xs uppercase tracking-[0.08em] text-zinc-500 font-medium ${
                      i === 0 ? 'text-center px-4 w-20' : i >= 4 && i <= 7 ? 'text-right px-4' : 'text-left px-4'
                    }`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((player) => (
                <tr
                  key={player.id}
                  data-testid={`player-row-${player.id}`}
                  className="transition-colors"
                  style={{ borderBottom: '1px solid #1F1F22' }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = 'rgba(39,39,42,0.4)')
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = 'transparent')
                  }
                >
                  <td
                    className="px-4 py-3 text-center text-emerald-400 font-medium"
                    style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '12px' }}
                  >
                    {player.current_rating.toFixed(0)}
                  </td>
                  <td className="px-4 py-3 font-semibold text-zinc-200">
                    {player.name}
                  </td>
                  <td className="px-4 py-3 text-zinc-400 text-sm">{player.country}</td>
                  <td className="px-4 py-3">
                    <SportBadge sport={player.sport} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <FormScoreBar score={player.recent_form_score} />
                  </td>
                  <td
                    className="px-4 py-3 text-right text-zinc-400"
                    style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '12px' }}
                  >
                    {player.surface_rating_hard?.toFixed(0) || '—'}
                  </td>
                  <td
                    className="px-4 py-3 text-right text-zinc-400"
                    style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '12px' }}
                  >
                    {player.surface_rating_clay?.toFixed(0) || '—'}
                  </td>
                  <td
                    className="px-4 py-3 text-right text-zinc-400"
                    style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '12px' }}
                  >
                    {player.surface_rating_grass?.toFixed(0) || '—'}
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/giocatori/${player.id}`}
                      data-testid={`player-detail-link-${player.id}`}
                      className="flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 whitespace-nowrap transition-colors"
                    >
                      {t.common.profile} <ArrowRight size={11} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
